import React, { useContext } from 'react';
import { getContext } from 'common';
import CloseIcon from '@nokia-csf-uxr/ccfk-assets/CloseIcon';
import { RADIUS_2 } from '@nokia-csf-uxr/freeform-design-tokens/tokens/border-radius';
import { SPACING_8, SPACING_4 } from '@nokia-csf-uxr/freeform-design-tokens/tokens/spacing';
import _merge from 'lodash/merge';
import {
  SECONDARY_GLOBAL_BASE as DEFAULT_SECONDARY_GLOBAL_BASE,
  SECONDARY_GLOBAL_BACKGROUND as DEFAULT_SECONDARY_GLOBAL_BACKGROUND,
} from 'components/styles/global-colors/global-colors-default-theme';
import {
  SECONDARY_GLOBAL_BASE as DARK_SECONDARY_GLOBAL_BASE,
  SECONDARY_GLOBAL_BACKGROUND as DARK_SECONDARY_GLOBAL_BACKGROUND,
} from 'components/styles/global-colors/global-colors-dark-theme';
import {
  SECONDARY_GLOBAL_BASE as AAA_DEFAULT_SECONDARY_GLOBAL_BASE,
  SECONDARY_GLOBAL_BACKGROUND as AAA_DEFAULT_SECONDARY_GLOBAL_BACKGROUND,
} from 'components/styles/global-colors/global-colors-aaa-default-theme';
import {
  SECONDARY_GLOBAL_BASE as AAA_DARK_SECONDARY_GLOBAL_BASE,
  SECONDARY_GLOBAL_BACKGROUND as AAA_DARK_SECONDARY_GLOBAL_BACKGROUND,
} from 'components/styles/global-colors/global-colors-aaa-dark-theme';
import { LabelContext } from 'components/Label';
import IconButton, { IconButtonProps } from 'components/IconButton';

import Field from 'components/Field/';
import TextInputButton from './TextInputButton';
import TextInputLabelContent from './TextInputLabelContent';
import TextInputErrorMessage from './TextInputErrorMessage';

import Styled from './styles';

const getOutlinedPadding = () => ({ paddingLeft: SPACING_8, paddingRight: SPACING_8 });
const RADIUS = RADIUS_2;

const INPUT_STYLE = { height: '2rem', width: '100%' };

export interface TextInputProps extends Omit<React.ComponentPropsWithRef<'div'>, 'onChange'> {
  /**
   * Callback fired when the value is changed. Signature: `function(event: object) => void`,
   */
  onChange?: React.ChangeEventHandler<HTMLInputElement>;
  /** The value of the input element, required to control the components. Note: The native input element's `value` attribute will be set using this prop. */
  value: string;
  /** The variant of the input. */
  variant?: 'outlined' | 'underlined';
  /** If `true`, the component will be disabled. Note: The native input element's `disabled` attribute wills be set using this prop. Note: If the `disabled` prop is not set, it will be internally taken from the immediate parent `Label` component. */
  disabled?: boolean;
  /** If `true`, the field cannot be edited by the user. Note: The native input element's `readonly` attribute will be set using this prop.  Note: If the `readOnly` prop is not set, it will be internally taken from the immediate parent `Label` component. */
  readOnly?: boolean;
  /** The short hint displayed in the input when it is empty. Note: The native input element's `placeholder` attribute will be set using this prop. */
  placeholder?: string;
  /** If `true`, the component will automatically get focus when the page loads. Note: The native input element's `autofocus` attribute will be set using this prop. */
  autoFocus?: boolean;
  /** If set, the button will be rendered next to the field. `TextInputButton` component can be used. */
  button?: React.ReactNode;
  /** If `true`, the input will be rendered in the error state. (Also, input can be in error state if `errorMessage` is set.) */
  error?: boolean;
  /** If set, the message will be displayed in the error state. `TextInputErrorMessage` component can be used. */
  errorMessage?: React.ReactNode;
  /** If `true`, the max-width will be `31.25rem`. */
  maxWidth?: boolean;
  /** If `true`, the clear button will show. */
  showClearButton?: boolean;
  /** Props just for clear button. */
  clearButtonProps?: Partial<IconButtonProps>;
  /** Render a custom icon (to replace the default clear icon). See <IconButton /> and <Icon/> component for more information. */
  clearButtonIcon?: React.ReactNode;
  /** DOM attributes applied to the input element. Field validation is controlled by you and can be implemented by evaluating the value of the field, thus configuring `required`, `maxLength`, `minLength` and `pattern` attributes does enable the field validation. Note: DOM attributes should be camelCased. */
  inputProps?: Partial<React.ComponentPropsWithRef<'input'>> & { theme?: any };
  /** To override the button theme. Please refer to the "Readme" tab to learn more. */
  theme?: any;
}

/** Uncontrolled input field. Any other props than the `TextInput` props will be provided to the root element. To alter the DOM attributes applied to the internal `input` element, please use `inputProps`. To get the ref of the outermost element use `ref` prop. [Read React useRef hook documentation](https://reactjs.org/docs/hooks-reference. */
const TextInput = React.forwardRef<HTMLElement, TextInputProps>((props: TextInputProps, ref) => getContext(({ RTL, theme }) => {
  const {
    variant,
    onChange,
    value,
    disabled,
    readOnly,
    placeholder,
    inputProps,
    errorMessage,
    button,
    autoFocus,
    error,
    showClearButton,
    clearButtonProps,
    clearButtonIcon,
    theme: propTheme,
    ...wrapperProps
  } = props;
  const isDarkTheme = theme.NAME === 'dark';
  const isAAADarkTheme = theme.NAME === 'aaa-dark';
  const isAAADefaultTheme = theme.NAME === 'aaa-default';
  const hasButton = !!button;

  const clearButtonPosition = _merge({ position: 'absolute' as const, top: '0.375rem' }, RTL ? { left: SPACING_4 } : { right: SPACING_4 });

  const resolvedInputElProps = {
    type: 'text',
    style: variant === 'outlined' ? {
      borderRadius: RADIUS,
      ...getOutlinedPadding(),
      ...INPUT_STYLE
    } : INPUT_STYLE,
    ...inputProps
  };
  const bgTheme = {
    TEXT_INPUT: {
      OUTLINED: {
        background: isDarkTheme
          ? DARK_SECONDARY_GLOBAL_BASE
          : isAAADarkTheme
            ? AAA_DARK_SECONDARY_GLOBAL_BASE
            : isAAADefaultTheme
              ? AAA_DEFAULT_SECONDARY_GLOBAL_BASE
              : DEFAULT_SECONDARY_GLOBAL_BASE,
      },
      UNDERLINED: {
        background: isDarkTheme
          ? DARK_SECONDARY_GLOBAL_BACKGROUND
          : isAAADarkTheme
            ? AAA_DARK_SECONDARY_GLOBAL_BACKGROUND
            : isAAADefaultTheme
              ? AAA_DEFAULT_SECONDARY_GLOBAL_BACKGROUND
              : DEFAULT_SECONDARY_GLOBAL_BACKGROUND,
      },
    },
  };
  const resolvedTheme = _merge(
    {},
    variant === 'outlined' ?
      theme.TEXT_INPUT.OUTLINED :
      theme.TEXT_INPUT.UNDERLINED,
    variant === 'outlined'
      ? bgTheme.TEXT_INPUT.OUTLINED
      : bgTheme.TEXT_INPUT.UNDERLINED,
    propTheme
  );
  const labelContext = useContext(LabelContext);
  const resolvedDisabled = props.disabled !== undefined ? props.disabled : labelContext.disabled;
  const resolvedReadOnly = props.readOnly !== undefined ? props.readOnly : labelContext.readOnly;
  const isError = !!errorMessage || error;
  const hasErrorMessage = errorMessage !== undefined && (typeof errorMessage === 'object' || (errorMessage as string).length > 0);
  // Component's prop should override inputProps
  const resolvedFieldProps = {
    ...resolvedInputElProps,
    variant,
    onChange,
    value,
    disabled: resolvedDisabled,
    readOnly: resolvedReadOnly,
    placeholder,
    error: isError,
    hasButton,
    autoFocus,
    theme: resolvedTheme
  };

  const resolvedWrapperProps = {
    ...wrapperProps
  };

  return (
    <Styled.Wrapper {...resolvedWrapperProps} RTL={RTL} ref={ref}>
      <Styled.RowWrapper>
        <Styled.FieldWrapper theme={resolvedTheme} >
          <Field {...resolvedFieldProps} />
          {showClearButton && value.length > 0 &&
          (
            <IconButton size="small" rippleStyle={clearButtonPosition} {...clearButtonProps}>
              { clearButtonIcon || <CloseIcon /> }
            </IconButton>
          )}
        </Styled.FieldWrapper>
        {hasButton && button}
      </Styled.RowWrapper>
      {hasErrorMessage &&
        <Styled.ErrorContainer>
          {typeof errorMessage === 'string' ? <TextInputErrorMessage theme={resolvedTheme}>{errorMessage}</TextInputErrorMessage> : errorMessage}
        </Styled.ErrorContainer>
      }
    </Styled.Wrapper>
  );
}));

TextInput.displayName = 'TextInput';

TextInput.defaultProps = {
  onChange: undefined,
  variant: 'outlined',
  disabled: undefined, // `disabled`, by default, needs to be undefined to find is it configured by user or not.
  readOnly: undefined, // `readOnly`, by default, needs to be undefined to find is it configured by user or not.
  placeholder: undefined,
  autoFocus: false,
  showClearButton: false,
  clearButtonProps: {},
  clearButtonIcon: undefined,
  error: false,
  errorMessage: undefined,
  button: undefined,
  maxWidth: false,
  inputProps: {
    type: 'text'
  },
  theme: {},
};

export {
  TextInput as default,
  TextInputButton,
  TextInputLabelContent,
  TextInputErrorMessage
};
